import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "reactstrap";
import { Dispatch } from "redux";
import { clearSelectedItem, setSelectedStartDate } from "../../store/actions/timesheet.actions";
import { IState } from "../../store/models/IState";
import { ITimesheetItem } from "../../store/models/ITimesheetItem";
import { ITimesheetState } from "../../store/models/ITimesheetState";
import { getAustraliaDateFormat } from "../../utils/utils";
import ApplicationError from "../common/ApplicationError";
import TimesheetList from "../timesheet/TimesheetList";
import TimesheetModal from "../timesheet/TimesheetModal";

export interface ITimesheetProps {

}

const Timesheet: React.FC<ITimesheetProps> = (props): JSX.Element => {
    const dispatch: Dispatch<any> = useDispatch();
    const timesheetState: ITimesheetState = useSelector((state: IState) => state.timesheetState);
    const selectedStartDate: Date = timesheetState.selectedStartDate;

    // Modals.    
    const [itemModalShow, setItemModalShow] = useState(false);

    // Month Picker.
    const [startDate, setStartDate] = useState(selectedStartDate);

    const defaultTooltipText = "Copied";
    // Text for the copy tooltip.
    const [tooltipText, setTooltipText] = useState(defaultTooltipText);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        setInterval(() => {
            setShowTooltip(false);
        }, 3000);
    }, [tooltipText, setShowTooltip]);

    /**
     * Clears the currently selected item then opens the modal.
     */
    function addItemClick(): void {
        dispatch(clearSelectedItem());
        showModal();
    }

    /**
     * Display the modal for adding.
     */
    function showModal(): void {
        setItemModalShow(true);
    }

    /**
     * Hide the modal.
     */
    function onCloseModal(): void {
        setItemModalShow(false);
    }

    /**
     * Sets the StartDate in state.
     * @param date The date selected in the DatePicker.
     * @param event The event that fired when the user selected a Date.
     */
    function onDateChange(date: Date, event: React.SyntheticEvent<any> | undefined): void {
        if (date && date != null) {
            setStartDate(date);

            // Dispatch to redux so that new items are retrieved.
            dispatch(setSelectedStartDate(date));
        }
    }

    /**
     * Gets the item data as a tab separated string.
     * @param currentData The current data as a string.
     * @param propertiesToCopy The properties to be added as tab separated properties.
     */
    function getItemData(currentData: string, propertiesToCopy: any[]): string {
        for (let i = 0; i < propertiesToCopy.length; i++) {
            const property = propertiesToCopy[i];
            currentData = `${currentData}${property}`;

            if (i < propertiesToCopy.length - 1) {
                // Don't add a tab after the last item.
                currentData = `${currentData}\t`;
            }
        }

        return currentData;
    }

    /**
     * Copies the expected properties from the item and comma separates them.
     * @param item
     */
    function copyItemData(item: ITimesheetItem, addNewLine: boolean): string {
        let result = "";
        const propertiesToCopy: any[] =
            [
                getAustraliaDateFormat(item.date),
                item.client,
                item.description,
                item.task,
                item.timeInHours,
            ];

        // Add item data.
        result = getItemData(result, propertiesToCopy);

        // Add a new line.
        if (addNewLine) {
            result = `${result}\n`;
        }
        
        return result;
    }

    /**
     * Copies the text from the list to the clipboard.
     */
    function copyClick(): void {
        let data = "";

        // Columns names for the header row.
        const propertyHeaders: string[] =
            [
                "Date",
                "Client",
                "Description",
                "Task",
                "Hours",
            ];

        // Slicing to copy the array items. Otherwise, the sorting affects the collection displayed in the list.
        let items = timesheetState?.items.slice();

        // Sort by Client and then Date ascending.
        items.sort((a, b) => a.date < b.date ? 1 : -1);
        items.sort((a, b) => a.client > b.client ? 1 : -1);

        for (let i = 0; i < items.length; i++) {
            if (i === 0) {
                // Add the header row.
                data = `${getItemData(data, propertyHeaders)}\n`;
            }

            const item = items[i];
            data = `${data}${copyItemData(item, i < items.length - 1)}`;
        }

        // Copy to clipboard.
        const element = document.createElement('textarea');
        element.value = data;
        document.body.appendChild(element);
        element.select();
        document.execCommand('copy');
        document.body.removeChild(element);

        setShowTooltip(true);
        setTooltipText(defaultTooltipText);
    }

    return (
        <div className="timesheet-container">
            <h1 className="d-none d-sm-none d-md-block">Timesheet Entries</h1>
            <p className="d-none d-sm-none d-md-block">Enter times for the selected month.</p>
            <ApplicationError />
            <div className="form-row">
                <div className="form-group col-9">
                    <DatePicker
                        id="input_start_date"
                        className="form-control"
                        selected={startDate}
                        onChange={onDateChange}
                        showPopperArrow={false}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        showFullMonthYearPicker
                    />
                </div>
                <div className="form-group col-3">
                    <div className="header-buttons float-right">
                        <OverlayTrigger
                            show={showTooltip}
                            placement="top"
                            overlay={<Tooltip id="copy-tooltip">{tooltipText}</Tooltip>}
                        >
                            <Button variant="secondary" className="action-link-button link-button" id="copyButton" onClick={() => copyClick()}><i className="fas fa-copy"></i></Button>
                        </OverlayTrigger>
                        <Button variant="secondary" className="action-link-button link-button" id="addItemButton" onClick={() => addItemClick()}><i className="fas fa-plus"></i></Button>
                    </div>
                </div>
            </div>
            <TimesheetList isReadOnly={false} onSelected={showModal} />
            <TimesheetModal onHide={onCloseModal} show={itemModalShow} />
        </div>
    )
}

export default Timesheet;